//
// ChatChange - Component for displaying chat change/event messages
// Handles various types of case events like category changes, user actions,
// emergency alerts, calls, questionnaire responses, etc.
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Fragment,
  memo,
} from 'react';

import {
  NoWarning,
  Warning,
  TelephonePickup,
  TelephoneHangup,
} from '../../../../assets/js/svg/gui';
import {
  displayJson,
  logError,
} from '../../../../utils';
import {
  Column,
  Row,
} from '../../../display';
import SvgColorImg from '../../../SvgColorImg';

import ChatMessageControls from './ChatMessageControls';
import ChatTaskEvent from './ChatTaskEvent';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  MessageFrame,
  MessageText,
} from './components';

const ChatChange = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  visibleInThisGathering = true,
  participantsRef,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  myCrUid,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const {
    id,
    gthrId,
    msgRdatim1,
    qstnId,
    ccatCode,
    ccatPath,
    csLvl,
    close,
    reject,
    hold,
    handle,
    leave,
    videoEn,
    crClose,
    crCancel,
    crHold,
    crSolved,
    addGcatCode,
    addGrpGpid,
    emergency,
    crUid,
    join,
    conflict,
    call,
    statement,
    block,
    task,
  } = message;

  const content = [];
  const feedback = false;
  let hide = false;
  const withChatAuthorDateHeader = true;
  const fullWidth = false;

  if (statement) {
    const statementTxt = translate(qstnId, 'statement');

    content.push(
      <MessageText
        key="qstn"
        author={author}
        italic
        message={statementTxt}
      />,
    );
  } else {
    //
    // Category
    //
    if (ccatCode !== undefined) {
      const ccatArray = (ccatPath)
        ? ccatPath.split('/')
        : [];
      ccatArray.push(ccatCode);

      content.push(
        <MessageText
          key="ccatcat"
          author={author}
          message={translate('eventCaseCat', 'chat')}
        />,
        <MessageText
          key="ccatpath"
          author={author}
          message={(
            <Row fullw start={!!author} end={!author} gap={1} className="flex-wrap">
              {ccatArray
                .map((code, index) => (
                  <Fragment key={code}>
                    {(index)
                      ? <b key={`${code}__b`}>{' > '}</b>
                      : null}
                    <SvgColorImg
                      description={translate(code, 'userMainMenu')}
                      src={`/images/svg/cases/${code}.svg`}
                      key={`${code}__i`}
                      className="h-8 w-8 shrink-0"
                      imgColorAsCssFill
                    />
                  </Fragment>
                ))}
              {` ${translate(
                ccatArray[ccatArray.length - 1],
                'userMainMenu',
              )}`}
            </Row>
          )}
        />,
      );
    }

    //
    // Level
    //
    if (csLvl !== undefined) {
      let level;
      if (csLvl === 1) {
        level = translate('normal', 'status', undefined, false);
      } else if (csLvl === 2) {
        level = translate('urgent', 'status', undefined, false);
      } else if (csLvl === 3) {
        level = translate('critical', 'status', undefined, false);
      }
      const params = { level };

      content.push(
        <MessageText
          key="cslvl"
          author={author}
          message={translate('eventCaseLevel', 'chat', params)}
        />,
      );
    }

    //
    // Handling messages, and other simple properties
    //
    if ((close !== undefined)
        || reject
        || (hold !== undefined)
        || handle
        || (leave !== undefined)
        || videoEn) {
      let messageText;
      if (close) messageText = translate('eventCaseClose', 'chat');
      else if (hold) messageText = translate('eventCaseHold', 'chat');
      else if (reject) messageText = translate('eventCaseReject', 'chat');
      else if ((hold === false)
            && (close === false)) messageText = translate('eventCaseResume', 'chat');
      else if (handle) messageText = translate('eventCaseHandle', 'chat');
      else if (leave === true) messageText = translate('eventCaseLeft', 'chat');
      else if (leave === false) messageText = translate('eventCaseRejoin', 'chat');
      else if (videoEn === 1) messageText = translate('eventVideoEnable', 'chat');
      else if (videoEn !== 1) messageText = translate('eventVideoDisable', 'chat');

      if (messageText) {
        content.push(
          <MessageText
            key="handle"
            author={author}
            message={messageText}
          />,
        );

        // fullWidth = true;
      }
    }

    //
    // User case handling
    //
    if (crClose || crCancel || crHold || crSolved) {
      let messageText;
      if (crHold) messageText = translate('eventUserPause', 'chat');
      else if (crClose) messageText = translate('eventUserClose', 'chat');
      else if (crCancel) messageText = translate('eventUserCancel', 'chat');

      if (messageText) {
        content.push(
          <MessageText
            key="userHandling"
            author={author}
            message={messageText}
          />,
        );

        // fullWidth = true;
      }
    }

    //
    // Attendants
    //
    if (addGcatCode || addGrpGpid) {
      let messageText = `${translate('eventAddHandlers', 'chat')}: `;
      let first = true;
      if (addGcatCode && addGcatCode.length) {
        addGcatCode.forEach((thisGcatCode) => {
          if (!first) messageText += ', ';
          else first = false;
          messageText += translate(thisGcatCode);
        });
      }
      if (addGrpGpid && addGrpGpid.length) {
        addGrpGpid.forEach((thisGrpGpid) => {
          if (!first) messageText += ', ';
          else first = false;
          const thisGroup = groupsMap.get(thisGrpGpid);
          if (thisGroup) messageText += thisGroup.grpDesc;
          else messageText += '??';
        });
      }

      if (messageText) {
        content.push(
          <MessageText
            key="addHandlers"
            author={author}
            message={messageText}
          />,
        );
      }
    }

    //
    // Emergency
    //
    if (emergency !== undefined) {
      if (crUid === undefined) {
        content.push(
          <MessageText
            key="emergency"
            author={author}
            message={(
              <>
                {(emergency) ? <Warning /> : <NoWarning className="drop" /> }
                <i>
                  {(emergency)
                    ? translate('eventUserEmergency', 'chat')
                    : translate('eventUserNoEmergency', 'chat')}
                </i>
              </>
            )}
          />,
        );
      } else {
        hide = true;
      }
    }

    //
    // User joining
    //
    if (join) {
      const {
        as: joinAs, supported, reporter, target, whileOnPhone,
      } = join;

      const params = {
        name: author,
      };
      const other = supported || reporter || target;
      if (other) {
        const otherParticipant = participantsRef.current[other.crUid];
        if (otherParticipant) {
          params.targetName = `${otherParticipant.crFname} ${otherParticipant.crLname}`;
        } else {
          params.targetName = translate('missing', 'errors');
        }
      }

      let messageText;
      if (author) {
        if (whileOnPhone) params.targetName = `${whileOnPhone}`;
        messageText = (whileOnPhone && (joinAs === 'creator'))
          ? translate(`joinAsPhone${joinAs}`, 'chat', params)
          : translate(`joinAs${joinAs}`, 'chat', params);
      } else {
        messageText = translate(`meJoinAs${joinAs}`, 'chat', params);
      }

      let icon = null;
      if (whileOnPhone && (joinAs === 'creator')) {
        icon = <TelephonePickup className="black" />;
      }

      if (messageText) {
        content.push(
          <MessageText
            key="join"
            author={author}
            message={(
              <>
                {icon}
                <i>{messageText}</i>
              </>
            )}
          />,
        );
      }
    }

    //
    // Users conflict
    //
    if (conflict) {
      if (crUid === undefined) {
        content.push(
          <div
            key="conflict"
            className="flex flex-row gap-2 items-center"
          >

            <div
              className="bg-white rounded-full p-0.5 border-pxUiAlert border-2"
            >
              <SvgColorImg
                src="/images/svg/gui/stop.svg"
                imgColor="#FF0000"
                description="stop"
                className="h-6 w-6"
              />
            </div>

            <MessageText
              author={author}
              message={translate('eventUserConflictUsrs', 'chat')}
            />
          </div>,
        );

        // fullWidth = true;
      } else {
        hide = true;
      }
    }

    //
    // Task workflow event
    //
    if (task) {
      content.push(
        <ChatTaskEvent
          key="task"
          task={task}
          author={author}
          participantsRef={participantsRef}
          groupsMap={groupsMap}
        />,
      );
    }

    //
    // User being blocked
    //
    if (block) {
      const { src, target, release } = block;
      const { crUid: tgtCrUid } = target;

      let messageText;
      if (tgtCrUid === myCrUid) {
        messageText = translate(
          release ? 'eventUserUnbloqued' : 'eventUserBloqued',
          'chat',
        );
      } else {
        const {
          crUid: srcCrUid,
          csid: srcCsid,
          // cuid: srcCuid,
        } = src;

        const targetUser = participantsRef.current[tgtCrUid];
        const targetName = targetUser?.name || translate('missing', 'errors');

        // if (!targetUser?.crFname) {
        //   console.log(`targetUser=${displayJson(targetUser)}`);
        // }

        if (srcCrUid) {
          if (srcCrUid === myCrUid) {
            messageText = translate(
              release ? 'youUnblockedTgt' : 'youBlockedTgt',
              'chat',
              { tgt: targetName },
            );
          } else {
            const srcUser = participantsRef.current[srcCrUid];
            const srcName = (srcUser)
              ? `${srcUser.crFname} ${srcUser.crLname}`
              : translate('missing', 'errors');

            // console.log(`srcUser=${displayJson(srcUser)}\n src=${
            //   displayJson(src)} myCrUid=${myCrUid}`);

            messageText = translate(
              release ? 'srcUnblockedTgt' : 'srcBlockedTgt',
              'chat',
              { src: srcName, tgt: targetName },
            );
          }
        } else if (srcCsid) {
          messageText = translate(
            release ? 'tgtGotUnblockedInOtherCase' : 'tgtGotBlockedInOtherCase',
            'chat',
            { tgt: targetName },
          );
        } else {
          messageText = translate(
            release ? 'tgtGotUnblockedByCommunity' : 'tgtGotBlockedByCommunity',
            'chat',
            { tgt: targetName },
          );
        }
      }

      content.push(
        <MessageText
          key="block"
          author={author}
          message={messageText}
        />,
      );
    }
  }

  //
  // Audio call event
  //
  if (call) {
    const { phone } = call;

    const params = {};
    const reqParticipant = participantsRef.current[call.reqCrUid];
    if (reqParticipant) {
      params.caller = reqParticipant.name;

      const ackParticipant = participantsRef.current[call.ackCrUid]
                            || participantsRef.current[call.tgtCrUid];

      if (ackParticipant) params.called = ackParticipant.name;

      if (phone) params.number = phone.toString();
    }

    const messageText = (phone)
      ? translate(`callPhone${call.event}`, 'chat', params)
      : translate(`call${call.event}`, 'chat', params);

    let icon = null;
    if (phone) {
      if (call.event === 'end') icon = <TelephoneHangup />;
    }

    if (crUid === undefined) {
      content.push(
        <MessageText
          key="call"
          author={author}
          message={(
            <>
              {icon}
              <i>{messageText}</i>
            </>
          )}
        />,
      );
    } else {
      hide = true;
    }
  }

  if ((!content.length) && (!hide)) {
    content.push(`-- unknown message ${JSON.stringify(message)} --`);

    logError(
      `ChatChange unknown message ${displayJson(message)}`,
      'ChatChange.unknownMessage',
      {
        msgId: message?.id,
        keys: message && Object.keys(message),
      },
    );
  }

  if (!content.length) return null;

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      {(withChatAuthorDateHeader)
        ? (
            <ChatAuthorName
              translate={translate}
              author={author}
              initials={initials}
              crBlocked={crBlocked}
              gcatCode={gcatCode}
              grpNbr={grpNbr}
              prtnrLogoUrl={prtnrLogoUrl}
            />
          )
        : null}

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        {(withChatAuthorDateHeader)
          ? (
              <ChatAuthorIcon
                author={author}
                initials={initials}
                color={color}
                gcatCode={gcatCode}
                grpGpid={grpGpid}
                grpLbl={grpLbl}
                groupsMap={groupsMap}
                imagesMap={imagesMap}
              />
            )
          : null}

        <MessageFrame
          key={id}
          author={author}
          visibleInThisGathering={visibleInThisGathering}
          fullWidth={fullWidth}
          feedback={feedback}
          event
        >
          {content}
        </MessageFrame>

        {(withChatAuthorDateHeader)
          ? (
              <Column
                className={classNames(
                  'self-start',
                  {
                    'order-2': !author,
                    'order-4': author,
                  },
                )}
                gap={1}
                crossStart={author}
                crossEnd={!author}
              >
                <ChatDate
                  dtime={msgRdatim1}
                  myMessage={!author}
                />

                <ChatMessageControls
                  translate={translate}
                  message={message}
                  author={author}
                />
              </Column>
            )
          : null}

      </Row>

    </div>
  );
});
ChatChange.displayName = 'ChatChange';

ChatChange.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  visibleInThisGathering: PropTypes.bool,
  participantsRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    // msgTxt: PropTypes.string.isRequired,
    msgRdatim1: PropTypes.string.isRequired,
    // thanks: PropTypes.string,
    statement: PropTypes.string,
    qstnId: PropTypes.string,
    ccatPath: PropTypes.string,
    ccatCode: PropTypes.string,
    csLvl: PropTypes.number,
    close: PropTypes.bool,
    hold: PropTypes.bool,
    reject: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    handle: PropTypes.number,
    leave: PropTypes.number,
    videoEn: PropTypes.number,
    crHold: PropTypes.number,
    crSolved: PropTypes.number,
    crClose: PropTypes.number,
    crCancel: PropTypes.number,
    addGcatCode: PropTypes.arrayOf(PropTypes.string),
    addGrpGpid: PropTypes.arrayOf(PropTypes.string),
    crUid: PropTypes.number,
    emergency: PropTypes.number,
    conflict: PropTypes.number,
    block: PropTypes.shape({
      reason: PropTypes.string,
      release: PropTypes.bool,
      src: PropTypes.shape({
        crUid: PropTypes.number,
        rlid: PropTypes.string,
        csid: PropTypes.string,
        cuid: PropTypes.string,
      }),
      target: PropTypes.shape({
        rlid: PropTypes.string,
        crUid: PropTypes.number,
      }),
    }),
    join: PropTypes.shape({
      as: PropTypes.string,
      victim: PropTypes.shape({
        crUid: PropTypes.number,
      }),
      supported: PropTypes.string,
      reporter: PropTypes.string,
      src: PropTypes.shape({
        crUid: PropTypes.number,
      }),
      target: PropTypes.shape({
        crUid: PropTypes.number,
      }),
      whileOnPhone: PropTypes.number,
    }),
    call: PropTypes.shape({
      phone: PropTypes.number,
      event: PropTypes.number,
      reqCrUid: PropTypes.number,
      ackCrUid: PropTypes.number,
      tgtCrUid: PropTypes.number,
    }),
    task: PropTypes.shape({}),
  }).isRequired,
  myCrUid: PropTypes.number,
  prtnrLogoUrl: PropTypes.string,
  selectGathering: PropTypes.func,
};

export default ChatChange;
